import React from "react";
import {observer} from "mobx-react";
import {OrbitComponent} from "../base-components/orbit-component/orbit-component";
import {LogoBox} from "../user-menu/logo-box/logo-box.component";
import {ProfileBox} from "../profile-box/profile-box.component";
import {SideMenu} from "../side-menu/side-menu.component";
import {ClockAndLastLoginBoxComponent} from "../clock-and-last-login-box/clock-and-last-login-box.component";
import {LanguageSwitcherDropdownComponent} from "../language-switcher-dropdown/language-switcher-dropdown.component";
import {CyClass} from '../../../common/cy-classes';
import {DRAGGING, OPEN_MENU} from "../../themes/css-global-classes";
import {MobileMenuHeaderBox} from "./boxes/mobile/mobile-menu-header.box";
import {MobileBackBox} from "./boxes/mobile/back/mobile-back.box";
import {MobileBackLabelBox} from "./boxes/mobile/back/mobile-back-label.box";
import { FlexGrowItemBox } from "../../themes/architecture/css-helpers/flex";
import {MainNavCanvasBox} from "./boxes/main-nav-canvas/main-nav-canvas.box";
import {MainNavBox} from "./boxes/main-nav.box";
import {MainNavContentWrapperBox} from "./boxes/main-nav-content-wrapper.box";
import {MainNavMenuBox} from "./boxes/main-nav-menu.box";
import {RelatedSitesWrapperBox} from "./boxes/related-sites/related-sites-wrapper.box";
import {RelatedSiteLinkBox, RelatedSiteLink} from "./boxes/related-sites/related-site-link.box";
import classNames from "classnames";
import {Link} from "../raw-components/link/link";

@observer
export class MainNav extends OrbitComponent {
    private mainNavRef = React.createRef<HTMLDivElement>();

    componentDidMount() {
        super.componentDidMount();
        this.reaction(() => this.services.gameSearch.isSearchBoxOpened,
        isSearchBoxOpened => {
            if (isSearchBoxOpened && this.mainNavRef.current != null) {
                this.mainNavRef.current.scrollTop = 0;
            }
        });
    }

    private _renderRelatedSitesLinks() {
        if (this.services.device.isMobile) {
            return null;
        }
        let relatedSited = this.services.configuration.data.header.related_sites;
        if (!relatedSited) {
            return null;
        }
        return <RelatedSitesWrapperBox headerHeight={this.services.header.headerHeight}>
            {relatedSited.map((s) => <RelatedSiteLinkBox key={s.link}>
                <Link href={s.link} target="_blank" box={RelatedSiteLink} onClick={() => {
                    this.services.window.open(s.link, "_blank")
                }}>{s.text}</Link>
            </RelatedSiteLinkBox>)}
        </RelatedSitesWrapperBox>;
    }

    private _onLogoClick = () => {
        this.services.userMenu.closeMenu();
    };
    private _renderLogo() {
        if (!this.services.device.isMobile) {
            return (
                <LogoBox
                    onClick={this._onLogoClick}
                    isNavigation={true}
                    withAnalytics={true}
                    useDarkLogo={this.services.configuration.data?.generalSettings?.sideMenuDarkLogo || false}
                />
            );
        }
    }

    private _renderProfileBox() {
        if (!this.services.device.isMobile) {
            return <ProfileBox/>;
        }
    }

    private _renderMobileMenuHeader() {
        const BackIcon = this.services.theme.currentTheme.icons.backIcon;
        let backButtonHandler = () => this._hideMobileMenu();
        let backButtonUseLabel = false;
        let backButtonCyClass = "cy-close-mobile-menu-icon";
        if (this.services.gameSearch.isSearchBoxOpened) {
            backButtonHandler = () => this.services.gameSearch.closeSearchResult();
            backButtonUseLabel = true;
            backButtonCyClass = "cy-close-mobile-search-icon";
        }
        if (this.services.sideMenu.isMobileLanguageSwitcher) {
            backButtonHandler = () => this.services.sideMenu.closeMobileLanguageSwitcher();
            backButtonUseLabel = true;
            backButtonCyClass = CyClass('cy-lang-switcher-back');
        }

        if (this.services.mediaQuery.lAndBelow) {
            return (
                <MobileMenuHeaderBox>
                    <FlexGrowItemBox flexBasis="33%">
                        <MobileBackBox className={backButtonCyClass} onClick={backButtonHandler}>
                            <BackIcon />
                            { backButtonUseLabel ?
                                <MobileBackLabelBox>{this.services.translation.translate("BACK")}</MobileBackLabelBox>
                            : null }
                        </MobileBackBox>
                    </FlexGrowItemBox>
                    <FlexGrowItemBox flexBasis="33%">
                        <LogoBox onClick={() => this._hideMobileMenu()}/>
                    </FlexGrowItemBox>
                    <FlexGrowItemBox flexBasis="33%"></FlexGrowItemBox>
                </MobileMenuHeaderBox>
            );
        }

        return null;
    }

    private dragProperties = {isDragging:false, initialPointerX:0, initialNavMenuTransform: 0};
    private _onDrag(e: React.TouchEvent<HTMLDivElement>){

        const isMobile = this.services.device.isMobile;

        if(isMobile && this.mainNavRef?.current) {
            const isRTL = this.services.language.isRTLLanguage;
            const pointerX = e.touches[0].clientX;

            if (!this.dragProperties.isDragging) {
                this.dragProperties.isDragging = true;
                this.dragProperties.initialNavMenuTransform = this.services.window.getElementHorizontalTranslationAmount(this.mainNavRef.current);
                this.dragProperties.initialPointerX = pointerX;

                //Prevent main nav from animate, we want it to move along with pointer without animation
                this.mainNavRef.current.classList.add(DRAGGING);
            }

            //min/max (depending on RTL direction) to prevent from dragging to the opposite direction
            const pointerXDelta = Math.abs(Math[(isRTL ? 'max' : 'min')](pointerX, this.dragProperties.initialPointerX) - this.dragProperties.initialPointerX) * (isRTL ? -1 : 1);

            this.mainNavRef.current.style.transform = `translateX(${this.dragProperties.initialNavMenuTransform - pointerXDelta}px)`;
        }
    }

    private _onStopDragging(){
        if(this.dragProperties.isDragging) {
            const isRTL = this.services.language.isRTLLanguage;

            const windowSize = isRTL ? this.services.window.innerWidth : 0;

            //Close after 1/4 of menu is outside viewport
            const minMainNavXToAutoClose = Math.round(this.mainNavRef.current.clientWidth * (1/4));
            const currentMainNavXPosition = Math.abs(windowSize - this.mainNavRef.current.getBoundingClientRect()[isRTL ? 'right' : 'left']);

            //Let the main nav animate again
            this.mainNavRef.current.classList.remove(DRAGGING);
            this.dragProperties.isDragging = false;

            // The main nav is in position where it should auto close
            if (currentMainNavXPosition > minMainNavXToAutoClose) {
                this.services.sideMenu.closeMobileMenu();
            }

            //Remove dragging X position to let it open or close by logic
            this.mainNavRef.current.style.removeProperty("transform");
        }
    }



    render(): React.ReactNode {
        const cssClasses = classNames("cy-main-nav", {
            [OPEN_MENU]: this.services.sideMenu.isMobileMenuOpen
        });


        //export const MAIN_NAV = 'main-nav';


        //main-nav-menu
        //main-nav-menu

        return (
            <MainNavBox className={cssClasses} ref={this.mainNavRef} onTouchMove={this._onDrag.bind(this)} onTouchEnd={this._onStopDragging.bind(this)} zIndex={this.services.sideMenu.zIndex}>
                <MainNavCanvasBox>
                    {this._renderMobileMenuHeader()}

                    <MainNavContentWrapperBox>
                        {this._renderRelatedSitesLinks()}
                        {this._renderLogo()}
                        <MainNavMenuBox>
                            {this._renderProfileBox()}
                            <SideMenu />
                        </MainNavMenuBox>
                        {this.services.mediaQuery.lAndBelow ? null : <ClockAndLastLoginBoxComponent/>}
                        <LanguageSwitcherDropdownComponent isOpen={this.services.sideMenu.isMobileLanguageSwitcher}/>
                    </MainNavContentWrapperBox>
                </MainNavCanvasBox>
            </MainNavBox>
        );
    }

    private _hideMobileMenu() {
        this.services.analytics.sendOrbitUserFlowEvent("Navigation","NavBar", 'Close_Burger');
        this.services.gameSearch.closeSearchResult();
        this.services.sideMenu.closeMobileMenu();
    }
}
